import { useMemo, useState } from "react";
import { Reveal } from "@/components/Reveal";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";
import { useContent } from "@/context/ContentContext";
import { ROYAL_IMG, RoyalCTA, RoyalLiquidBg } from "@/components/royal/shared";

// Royal Signature — Gallery: liquid full-bleed header + filters + before/after sliders.
export default function GalleryRoyal() {
  const { content } = useContent();
  const s = content?.settings || {};
  const items = content?.gallery || [];
  const categories = useMemo(() => ["All", ...new Set(items.map((g) => g.category).filter(Boolean))], [items]);
  const [filter, setFilter] = useState("All");
  const filtered = filter === "All" ? items : items.filter((g) => g.category === filter);
  const comparisons = filtered.filter((g) => g.before_image && g.after_image);
  const photos = filtered.filter((g) => !(g.before_image && g.after_image));
  const heroBlur = parseInt(s.royal_hero_blur || "0", 10) || 0;
  const heroBg = s.royal_results_hero_image || (items.find((g) => g.after_image)?.after_image || items[0]?.image) || ROYAL_IMG.clinic;

  return (
    <div className="bg-[#FAF8F5]" data-testid="gallery-page">
      <section className="relative flex min-h-[48vh] items-center overflow-hidden bg-[#F1EBE2]" data-testid="gallery-hero">
        <RoyalLiquidBg src={heroBg} blur={heroBlur} testid="gallery-hero-image" />
        <div className="relative mx-auto w-full max-w-[1400px] px-4 sm:px-8 lg:px-12">
          <div className="max-w-xl py-16 lg:py-20">
            <Reveal>
              <p className="eyebrow-royal" data-testid="gallery-eyebrow">Real Results</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="mt-4 font-serif text-4xl font-light leading-[1.08] tracking-tight text-[#0F0F0F] sm:text-5xl lg:text-6xl" data-testid="gallery-title">
                Real results. Real confidence.
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-lg text-sm font-normal leading-relaxed text-[#3A3630] sm:text-base">
                Unretouched patient outcomes from our treatment suites — drag the handle to compare before and after.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-4 pb-16 pt-12 sm:px-8 lg:px-12">
        {categories.length > 1 && (
          <Reveal delay={0.25}>
            <div className="mt-10 flex flex-wrap gap-2.5" data-testid="gallery-filters" role="tablist" aria-label="Filter gallery">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setFilter(c)}
                  role="tab"
                  aria-selected={filter === c}
                  data-testid={`gallery-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`rounded-full px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.18em] transition-all duration-300 ${
                    filter === c ? "bg-[#0F0F0F] text-white" : "border border-[#E8E2D9] text-[#4A4A4A] hover:border-[#C5A059] hover:text-[#C5A059]"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </Reveal>
        )}

        {comparisons.length > 0 && (
          <div className="mt-12 grid gap-10 md:grid-cols-2" data-testid="before-after-grid">
            {comparisons.map((g, i) => (
              <Reveal key={g.id} delay={i * 0.08}>
                <figure data-testid={`before-after-${g.id}`}>
                  <div className="overflow-hidden rounded-2xl border border-[#E8E2D9] bg-white p-2">
                    <BeforeAfterSlider before={g.before_image} after={g.after_image} title={g.title} />
                  </div>
                  <figcaption className="mt-4 flex items-baseline justify-between">
                    <span className="font-serif text-xl font-normal text-[#0F0F0F]">{g.title}</span>
                    <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#C5A059]">{g.category}</span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        )}

        {photos.length > 0 && (
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="gallery-grid">
            {photos.map((g, i) => (
              <Reveal key={g.id} delay={i * 0.06}>
                <figure className="group" data-testid={`gallery-item-${g.id}`}>
                  <div className="overflow-hidden rounded-2xl">
                    <img src={g.image} alt={g.title} loading="lazy" className="aspect-[4/5] w-full object-cover transition-transform duration-700 ease-liquid group-hover:scale-105" />
                  </div>
                  <figcaption className="mt-3 flex items-baseline justify-between">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#0F0F0F]">{g.title}</span>
                    <span className="text-[10px] font-light uppercase tracking-[0.15em] text-[#8A8580]">{g.category}</span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        )}
        {filtered.length === 0 && (
          <p className="mt-16 text-center text-sm font-light text-[#8A8580]" data-testid="gallery-empty">No results in this category yet.</p>
        )}
      </section>
      <RoyalCTA />
    </div>
  );
}
